import * as _ from "lodash";
import { useDeepCompareMemo } from "use-deep-compare";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Nullable, Units } from "@ctra/utils";

import { LibraryConfigType } from "../typings";
import { useChartContext } from "../providers";
import { useCallback, useMemo, useState } from "react";
import { PlotEvent } from "@ant-design/charts";

const {
  unitSystem: { unit: tUnit },
  chart: {
    tooltip: { contextLabel }
  }
} = Content;

/**
 * Get the common values from an array of objects
 * @param {Array<Nullable<Record<string, string | number>>>} array
 * @returns {{identical: {}, individual: Array<Nullable<Record<string, string | number>>>} | {identical: PartialObject<Record<string, string | number>>, individual: PartialObject<Record<string, string | number>>[]}}
 */
function findCommonValues(array: Array<Nullable<Record<string, string | number>>>) {
  if (_.size(_.compact(array))) {
    /**
     * Get the first item in the array
     * @type {Record<string, unknown>}
     */
    const [head] = _.compact(array);

    /**
     * Keep the keys
     * @type {string[]}
     */
    const keys = _.keys(head);

    /**
     * Filter keys that have the same value across all objects
     */
    const commonKeys = _.filter(keys, (key) =>
      _.every(array, (item) => _.isEqual(_.defaultTo(item, {})[key], head[key]))
    );

    /**
     * Create the common object with only common properties
     * @type {PartialObject<unknown>}
     */
    const identical = _.pick(head, commonKeys);

    /**
     * Create array of objects with only the unique properties
     * @type {PartialObject<Record<string, unknown>>[]}
     */
    const individual = _.map(array, (item) => _.omit(_.defaultTo(item, {}), commonKeys));

    return { identical, individual };
  }

  return {
    identical: {},
    individual: array
  };
}

/**
 * Make a divider for the tooltip
 * @param {"divider" | "summary"} type
 * @returns {{name: string, value: string}}
 */
const makeDivider = (type: "divider" | "summary"): { name: string; value: string } => ({
  name: `<div data-tooltip-purpose='${type}'></div>`,
  value: ""
});

/**
 * Remove private fields from the context
 * @param {T} context
 * @returns {Partial<T>}
 */
function removePrivateFields<T extends Record<string, unknown>>(context: T): Partial<T> {
  return _.omitBy<T>(context, (value, key) => _.startsWith(key, "_"));
}

/**
 * Tooltip DOM styles
 * @type {{"g2-tooltip": {}, "g2-tooltip-title": {color: string, margin: string}, "g2-tooltip-list": {}, "g2-tooltip-list-item": {display: string, flexDirection: string, alignItems: string, fontSize: string, whiteSpace: string}, "g2-tooltip-marker": {borderRadius: string, display: string, height: string, marginRight: string, width: string}, "g2-tooltip-value": {fontWeight: string}, "g2-tooltip-name": {}}}
 */
const tooltipDOMStyles = {
  "g2-tooltip": {},
  "g2-tooltip-title": {
    color: "#666",
    margin: "10px 0 10px 16px"
  },
  "g2-tooltip-list": {},
  "g2-tooltip-list-item": {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    fontSize: "14px",
    whiteSpace: "nowrap"
  },
  "g2-tooltip-marker": {
    borderRadius: "50%",
    display: "inline-block",
    height: "8px",
    marginRight: "8px",
    width: "8px"
  },
  "g2-tooltip-value": {
    fontWeight: "bold"
  },
  "g2-tooltip-name": {}
};

/**
 * Tooltip container template
 * @type {string}
 */
const tooltipContainerTpl = `
  <div class="g2-tooltip">
    <ul class="g2-tooltip-list"></ul>
    <div class="g2-tooltip-title"></div>
  </div>
`;

/**
 * Tooltip item template
 * @type {string}
 */
const tooltipItemTpl = `
  <li class="g2-tooltip-list-item" data-index={index}>
    <span class="g2-tooltip-marker" style="background-color: {color};"></span>
    <span class="g2-tooltip-name">{name}</span>
    <span class="g2-tooltip-value">{value}</span>
  </li>
`;

/**
 * These configs are common to all charts
 * @todo rename `commonConfig` to `config`
 */
export function useCommonConfig<T extends LibraryConfigType = LibraryConfigType>(): { commonConfig: T } {
  const {
    values: { meta: m, entities, variants },
    consolidatedValues: { series }
  } = useChartContext();

  const { t } = useTranslation();

  const [hiddenLegends, setHiddenLegends] = useState<Array<string>>([]);

  if (!_.size(entities)) {
    throw new Error("No chart entities in the context. How?");
  }

  /**
   * Make a lookup from the point context name to the unit
   * @type {Dictionary<any>}
   */
  const pointContextDataTypes = useDeepCompareMemo(() => {
    /**
     * Map the point context from the metadata for the main and the overlay charts
     * @type {any[]}
     */
    const pointContexts = _.flatMap(m, "pointContexts");

    return _.mapValues(_.keyBy(pointContexts, "name"), "unit");
  }, [m]);

  /**
   * Count the number of items in the legend
   * @type {number}
   */
  const legendSize = _.size(series);

  /**
   * Legend config
   * @type {{layout: string, position: string, flipPage: boolean, maxItemWidth: number, maxRow: number, offsetX: number, reversed: boolean, itemName: {formatter: (text: string) => void}}}
   */
  const legendConfig = useDeepCompareMemo(() => {
    return {
      radio: {
        tip: ""
      },
      layout: "horizontal",
      position: "top-left",
      flipPage: true,
      maxRow: 3,
      maxItemWidth: 1,
      offsetX: 24,
      reversed: true,
      itemName: {
        formatter: (text: string) => {
          return _.get(series, [text], text);
        }
      },
      selected: _.reduce(
        hiddenLegends,
        (acc, id) => {
          acc[id] = false;

          return acc;
        },
        _.mapValues(series, _.stubTrue) as Record<string, boolean>
      )
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, hiddenLegends]);

  return useDeepCompareMemo<{ commonConfig: any }>(
    () => ({
      commonConfig: {
        onReady: (plot: any) => {
          plot.on("legend-item:click", (evt: PlotEvent) => {
            const legendItem = evt.target.get("delegateObject");

            if (legendItem) {
              const {
                legend: {
                  cfg: { items }
                }
              } = legendItem;

              setHiddenLegends(_.map(_.filter(items, "unchecked"), "id"));
            }
          });
        },
        animation: false,
        interactions: [{ type: "element-active" }],
        /**
         * Basic legend settings
         */
        legend: legendConfig,
        /**
         * Basic tooltip settings
         */
        tooltip: {
          shared: legendSize <= 3,
          position: "top",
          customItems: (itemList: any[]) => {
            /**
             * Keep only visible items
             * @type {{}[]}
             */
            const visible = _.reject(itemList, (item) =>
              _.get(item, ["data", "context", "_hideFromTooltip"])
            );

            /**
             * Format the values using the Y axis unit
             * @type {any[]}
             */
            const withFormattedValues = _.map(visible, (item) => {
              /**
               * `item` Examples to not get lost
               * #1 { name: "650c87c9600c2a821dd6ce14.lyingMins", title: title: "06/04/2025" }
               * #2 { name: "Temperature (C)", title: "153" }
               * #3 { name: "Milk yield", title: "06/04/2025" }
               */

              let variantID: string;

              if (_.size(variants) === 1) {
                const [variant] = _.values(variants);
                variantID = variant.id;
              } else {
                const [id] = _.split(item.name, ".");
                variantID = id;
              }

              const meta = _.find(m, ["variantId", variantID]);
              const unit = _.get(meta, ["yAxis", "unit"], null);

              const precision = _.get(
                _.find(variants, ["id", variantID]),
                ["valueProperties", "decimals"],
                2
              );

              return {
                ...item,
                name: _.get(series, [item.name], item.name),
                value: unit
                  ? Units.format(item.value, unit, t<string>(tUnit(unit)), { asIs: true, precision })
                  : item.value
              };
            });

            /**
             * Tell whether the data point has context values
             * @type {boolean}
             */
            const hasContext: boolean = _.every(withFormattedValues, (item) =>
              _.has(item, ["data", "context"])
            );

            if (hasContext) {
              /**
               * Format the context values
               * @param {string | number} value
               * @param {string} key
               * @returns {{data: {}, mappingData: {}, name: any, value: string | number}}
               */
              const formatContextValue = (
                value: string | number,
                key: string
              ): {
                data: Record<string, unknown>;
                mappingData: Record<string, unknown>;
                name: string;
                value: string | number;
              } => ({
                data: {},
                mappingData: {},
                name: t<string>(contextLabel(key)),
                value: pointContextDataTypes[key]
                  ? Units.format(
                      value,
                      pointContextDataTypes[key],
                      t<string>(tUnit(pointContextDataTypes[key])),
                      { asIs: true }
                    )
                  : value
              });

              /**
               * See if the context is the same for all items
               * @type {Array<*>}
               */
              const uniqContextList: Array<Record<string, string | number>> = _.uniq(
                _.map(withFormattedValues, (item) => _.get(item, ["data", "context"], null))
              );

              // If there is one shared context, we drop the context after the original items
              if (_.size(_.compact(uniqContextList)) === 1) {
                const [context] = uniqContextList;
                const publicContext = removePrivateFields(context);

                if (_.size(publicContext)) {
                  return [
                    ...withFormattedValues,
                    makeDivider("divider"),
                    ..._.map(publicContext, formatContextValue),
                    makeDivider("divider")
                  ];
                } else {
                  return withFormattedValues;
                }
              }
              // Otherwise the context is different for each item
              else if (_.size(uniqContextList) > 1) {
                const { identical, individual } = findCommonValues(uniqContextList);

                const items = _.flatten(
                  _.map(withFormattedValues, (item, idx) => {
                    const context = individual[idx];
                    const publicContext = context ? removePrivateFields(context) : null;

                    return _.size(publicContext)
                      ? [
                          item,
                          makeDivider("divider"),
                          ..._.map(publicContext, formatContextValue),
                          makeDivider("divider")
                        ]
                      : item;
                  })
                );

                return _.size(identical)
                  ? [
                      ...items,
                      makeDivider("summary"),
                      ..._.map(removePrivateFields(identical), formatContextValue)
                    ]
                  : items;
              }
            }

            return withFormattedValues;
          },
          domStyles: tooltipDOMStyles,
          containerTpl: tooltipContainerTpl,
          itemTpl: tooltipItemTpl
        }
      }
    }),
    [hiddenLegends, m, series]
  );
}
